import { useCallback, useEffect, useRef, useState } from "react";
import { Eye, Heart } from "lucide-react";
import { api } from "./lib/api";
import { metricsCount, normalizeSellerMetrics, type ListingMetric } from "./data/metrics";
import "./metrics.css";

/** Mount once in the account view. ownerId invalidates the cache when the signed-in account changes. */
export function useSellerListingMetrics(enabled: boolean, ownerId = "") {
  const [byListing, setByListing] = useState<Record<string, ListingMetric>>({});
  const [available, setAvailable] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const generation = useRef(0);
  const refresh = useCallback(async (signal?: AbortSignal) => {
    if (!enabled) return;
    const current = ++generation.current;
    setLoading(true); setError(""); setByListing({}); setAvailable(null); setNote("");
    try {
      const result = normalizeSellerMetrics(await api.request<unknown>("/account/listing-metrics", { signal }));
      if (current === generation.current && !signal?.aborted) {
        setAvailable(result.available); setNote(result.note);
        if (result.available) setByListing(Object.fromEntries(result.items.map((item) => [item.listingId, item])));
      }
    } catch (value) {
      if (current === generation.current && !signal?.aborted) { setAvailable(false); setError(value instanceof Error ? value.message : "Le statistiche degli annunci non sono disponibili. Riprova."); }
    } finally { if (current === generation.current && !signal?.aborted) setLoading(false); }
  }, [enabled]);
  useEffect(() => {
    const controller = new AbortController();
    setByListing({}); setAvailable(null); setError(""); setNote(""); setLoading(false);
    if (enabled) void refresh(controller.signal);
    return () => { controller.abort(); generation.current += 1; };
  }, [enabled, ownerId, refresh]);
  return { byListing, available, loading, error, note, refresh };
}

export function ListingMetrics({ stats, loading = false }: { stats?: ListingMetric; loading?: boolean }) {
  const unavailable = !stats;
  const explanation = loading ? "Caricamento delle statistiche dell’annuncio." : unavailable ? "Statistiche dell’annuncio non disponibili." : "Visualizzazioni registrate; non persone uniche. Salvataggi attuali di account diversi dal venditore. Non sono mostrate le identità.";
  return <div className="seller-listing-metrics" aria-label={explanation} title={explanation}>
    <span><Eye size={14} aria-hidden="true" /><strong>{unavailable ? "—" : metricsCount(stats.viewCount)}</strong> visualizzazioni</span>
    <span><Heart size={14} aria-hidden="true" /><strong>{unavailable ? "—" : metricsCount(stats.saveCount)}</strong> salvataggi</span>
  </div>;
}
